import { z } from 'zod';
import { selfDisclosureAssetsStepSchema, selfDisclosureExpensesStepSchema, selfDisclosureFormSteps, selfDisclosureIncomeStepSchema, selfDisclosurePersonStepSchema, } from './self-disclosure.js';
/**
 * The public referrer intake `/t/<token>` („Selbstauskunft einreichen", owner decision 14.10.2026, D-248): a referrer
 * partner submits the self-disclosure of a prospect in one request. The four data steps are the customer form's
 * steps 1–4 with the identical validation (`selfDisclosure*StepSchema`); the SPA builds the fields from those schemas
 * and the label/option catalogs of `api/self-disclosure.ts` exactly like the customer form — the view only carries the
 * step order and titles. Step 5 is „Tippgeber" (the referrer's own data and the two confirmations) instead of the
 * customer's signature. Docs: docs/dev/api-referrer-partners.md.
 */
export const REFERRER_STEP_LABEL = 'Tippgeber';
export const REFERRER_INTAKE_TITLE = 'Selbstauskunft einreichen';
export const REFERRER_DATA_CORRECT_LABEL = 'Die Angaben sind nach bestem Wissen korrekt.';
/** „Der Interessent ist mit der Übermittlung seiner Daten an <Organisation> einverstanden." */
export function referrerProspectConsentLabel(organizationName) {
    return `Der Interessent ist mit der Übermittlung seiner Daten an ${organizationName} einverstanden.`;
}
export const REFERRER_NAME_MESSAGE = 'Bitte geben Sie Ihren Namen an.';
export const REFERRER_EMAIL_MESSAGE = 'Bitte geben Sie Ihre E-Mail-Adresse an.';
export const REFERRER_DATA_CORRECT_MESSAGE = 'Bitte bestätigen Sie, dass die Angaben korrekt sind.';
export const REFERRER_PROSPECT_CONSENT_MESSAGE = 'Bitte bestätigen Sie das Einverständnis des Interessenten.';
const brandSchema = z.object({
    name: z.string(),
    /** `#RRGGBB` or null (the form then falls back: partner → organization → DAMOKLES). */
    brandColor: z.string().nullable(),
    /** Public image URL (`/api/public/referrer-intake/<token>/logo/<partner|organization>`) or null. */
    logoUrl: z.string().nullable(),
});
export const referrerIntakeStepSchema = z.object({
    key: z.enum([...selfDisclosureFormSteps, 'referrer']),
    number: z.number().int(),
    label: z.string(),
});
export const referrerIntakeViewSchema = z.object({
    partner: brandSchema,
    organization: brandSchema.extend({
        /** `/legal/<slug>/privacy` or null while no privacy text is published. */
        privacyUrl: z.string().nullable(),
        imprintUrl: z.string().nullable(),
    }),
    /** The five steps in order: Person, Einkommen, Ausgaben, Vermögen, Tippgeber. */
    steps: z.array(referrerIntakeStepSchema),
});
export const referrerIntakeReferrerSchema = z.object({
    name: z
        .string({ error: REFERRER_NAME_MESSAGE })
        .trim()
        .min(2, { error: REFERRER_NAME_MESSAGE })
        .max(120, { error: REFERRER_NAME_MESSAGE }),
    email: z
        .string({ error: REFERRER_EMAIL_MESSAGE })
        .trim()
        .toLowerCase()
        .email({ error: 'Bitte geben Sie eine gültige E-Mail-Adresse an.' })
        .max(200),
    phone: z.string().trim().max(40).optional(),
    note: z.string().trim().max(2000).optional(),
});
/** The prospect must be reachable: step 1 needs an e-mail address or a phone number (the contact is matched by them). */
export const REFERRER_PROSPECT_CHANNEL_MESSAGE = 'Bitte geben Sie eine E-Mail-Adresse oder Telefonnummer des Interessenten an.';
export const referrerIntakeSubmitBodySchema = z
    .object({
    person: selfDisclosurePersonStepSchema,
    income: selfDisclosureIncomeStepSchema,
    expenses: selfDisclosureExpensesStepSchema,
    assets: selfDisclosureAssetsStepSchema,
    referrer: referrerIntakeReferrerSchema,
    consents: z.object({
        dataCorrect: z.literal(true, { error: REFERRER_DATA_CORRECT_MESSAGE }),
        prospectConsented: z.literal(true, { error: REFERRER_PROSPECT_CONSENT_MESSAGE }),
    }),
})
    .superRefine((value, ctx) => {
    if (!value.person.email && !value.person.phone) {
        ctx.addIssue({ code: 'custom', path: ['person', 'email'], message: REFERRER_PROSPECT_CHANNEL_MESSAGE });
    }
});
export const referrerIntakeResultSchema = z.object({
    ok: z.literal(true),
    /** The prospect's display name („Anna Berger") for the confirmation page. */
    reference: z.string(),
});
//# sourceMappingURL=referrer-intake.js.map